// @flow
import React, { useCallback, useEffect } from "react";
import { connect } from "react-redux";
import { branch, renderNothing } from "recompose";

import { withI18n, Translate } from "@quantum/i18n";

import { compose, difference, intersection, isEmpty, prop } from "ramda";

import SelecaoPeriodicidade from "./SelecaoPeriodicidade";
import SelectInput from "../../../comum/SelectInput";
import type {
  Periodo,
  Id,
  ErrosSelecao,
  ChaveEnum,
  MedidaSelecionada,
} from "../../../../model";
import {
  type State,
  type ActionCreator,
  selecionarPeriodo,
  i18n,
  isMedidaQualiSelecionada,
  getPeriodosIndividuais,
  getIndiceMedidaEditada,
  getIsIndicadorFinanceiroSelecionado,
  getListaPeriodosVisiveis,
  getMedidaSelecionadaEdicao,
  getMedidasReferenciadasEmExpressoes,
  getModoApp,
  getModoExibicaoMedidaSelecionada,
  getIdMedidaSelecionada,
  getPeriodosIndividuaisSelecionados,
  getPeriodosSelecionados,
} from "../../../../state";
import AreaPeriodosEstendido, {
  validacaoPeriodosEstendidos,
} from "./AreaPeriodosEstendido";

import { comEdicaoDeMedida } from "../../Contexto";

/*Criar um objeto com a seleção do período, que será desmembrado em uma ou mais medidas, que também terão um objeto desses, mas que representam apenas uma seleção.
Por exemplo 'dia a dia', que durante a edição terá duas datas que formam um range. Esse range criará medidas cujo objeto periodo é igual, mas tem datas que representam apenas um dia.
*/

type Prop = {
  listaPeriodos: Array<Periodo>,
  periodosIndividuais: Array<Id>,
  periodosIndividuaisSelecionados: Array<Id>,
  periodosSelecionados: Array<Id>,
  exibirPeriodicidade: boolean,
  desmarcarTodosPeriodos: ActionCreator,
  selecionarPeriodo: ActionCreator,
  validacaoSelecaoPeriodo: any => ErrosSelecao,
  medidaSelecionada: ?MedidaSelecionada,
  modoExibicaoMedidaSelecionada: ?ChaveEnum,
  indiceMedidaEditada: ?number,
  idMedidaSelecionada: ?number,
  medidasReferenciadasEmExpressoes: Set<string>,
};

function SelecaoPeriodo({
  listaPeriodos,
  periodosIndividuais,
  periodosIndividuaisSelecionados = [],
  periodosSelecionados = [],
  exibirPeriodicidade = true,
  desmarcarTodosPeriodos,
  selecionarPeriodo,
  validacaoSelecaoPeriodo,
  medidaSelecionada,
  modoExibicaoMedidaSelecionada,
  indiceMedidaEditada,
  idMedidaSelecionada,
  medidasReferenciadasEmExpressoes,
}: Prop) {
  useEffect(() => {
    const periodos = listaPeriodos.map(prop("id"));
    const periodosSelecionadosForaDaListaDePeriodos = difference(
      periodosSelecionados,
      periodos
    );

    if (!isEmpty(periodosSelecionadosForaDaListaDePeriodos)) {
      selecionarPeriodo(intersection(periodosSelecionados, periodos));
    }
  }, [listaPeriodos, periodosSelecionados, selecionarPeriodo]);

  const isOptionDisabled = useCallback(
    (id: Id) => {
      if (periodosIndividuaisSelecionados.length !== 0) {
        return !periodosIndividuaisSelecionados.includes(Number(id));
      } else if (periodosSelecionados.length !== 0) {
        return periodosIndividuais.includes(Number(id));
      }

      return false;
    },
    [periodosIndividuais, periodosIndividuaisSelecionados, periodosSelecionados]
  );

  return (
    <div className="ConfiguracaoMedida--row">
      <div className="ConfiguracaoMedida">
        <div className="ConfiguracaoMedida__label">
          <Translate>maiusculas.periodo</Translate>
          {periodosSelecionados.length >= 1 && (
            <span
              className="ConfiguracaoMedida__label--desmarcar"
              onClick={desmarcarTodosPeriodos}
            >
              <Translate>Comum.desmarcarTodos</Translate>
            </span>
          )}
        </div>
        <SelectInput
          onChange={selecionarPeriodo}
          value={periodosSelecionados}
          options={listaPeriodos}
          size={10}
          multiple={true}
          campoDescricao={"traducao"}
          isOptionDisabled={isOptionDisabled}
        />
      </div>
      <AreaPeriodosEstendido />
      {exibirPeriodicidade ? <SelecaoPeriodicidade /> : null}
    </div>
  );
}

export const criarFuncaoValidacaoSelecaoPeriodo = (state: State) => () => {
  if (
    isMedidaQualiSelecionada(state) ||
    getIsIndicadorFinanceiroSelecionado(state) ||
    getModoApp(state) === "SERIES"
  ) {
    return null;
  }

  const { translate } = i18n(state);

  const errosSelecaoPeriodo: ErrosSelecao = {};
  const errosSelecaoPeriodosEstendidos: ErrosSelecao = validacaoPeriodosEstendidos(
    state
  );

  const periodosSelecionados: ?Array<Id> = getPeriodosSelecionados(state);
  if (periodosSelecionados && periodosSelecionados.length === 0) {
    errosSelecaoPeriodo.periodoNaoSelecionado = translate(
      "Periodo.Msg.ErroPeriodoNaoSelecionado"
    );
  } else if (periodosSelecionados && periodosSelecionados.length > 1) {
    const periodosIndividuaisSelecionados: ?Array<Id> = getPeriodosIndividuaisSelecionados(
      state
    );

    if (
      periodosIndividuaisSelecionados &&
      periodosIndividuaisSelecionados.length !== 0
    ) {
      errosSelecaoPeriodo.selecaoPeriodosInvalida = translate(
        "Periodo.Msg.ErroPeriodoSelecaoIndividual",
        [periodosIndividuaisSelecionados.join(",")]
      );
    }
  }

  const erros = { ...errosSelecaoPeriodo, ...errosSelecaoPeriodosEstendidos };
  return Object.keys(erros).length === 0 ? null : erros;
};

export default compose(
  withI18n,
  comEdicaoDeMedida,
  branch(({ exibirSelecaoPeriodo }) => !exibirSelecaoPeriodo, renderNothing),
  connect(
    (state, { translate }) => {
      const indiceMedidaEditada = getIndiceMedidaEditada(state);

      return {
        periodosIndividuais: getPeriodosIndividuais(state),
        periodosIndividuaisSelecionados: getPeriodosIndividuaisSelecionados(
          state
        ),
        periodosSelecionados: getPeriodosSelecionados(state),
        listaPeriodos: getListaPeriodosVisiveis(state).map(periodo => ({
          ...periodo,
          traducao: translate(periodo.chaveTraducao) || periodo.chaveTraducao,
        })),
        validacaoSelecaoPeriodo: criarFuncaoValidacaoSelecaoPeriodo(state),
        medidaSelecionada:
          indiceMedidaEditada &&
          getMedidaSelecionadaEdicao(state, indiceMedidaEditada),
        modoExibicaoMedidaSelecionada: getModoExibicaoMedidaSelecionada(state),
        indiceMedidaEditada,
        idMedidaSelecionada: getIdMedidaSelecionada(state),
        medidasReferenciadasEmExpressoes: getMedidasReferenciadasEmExpressoes(
          state
        ),
      };
    },
    { selecionarPeriodo, desmarcarTodosPeriodos: () => selecionarPeriodo([]) }
  )
)(SelecaoPeriodo);
